
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSearchParams } from "react-router-dom";
import { isPast } from "date-fns";
import { fetchUserSubscriptions } from "@/lib/supabase";
import { bookingSchema, FormValues } from "../types";
import { useAuth } from "@/lib/hooks/auth";
import { useLoadUserData } from "./useLoadUserData";
import { useInitializeForm } from "./useInitializeForm";

export const useBookingFormInit = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const classId = searchParams.get("classId") || searchParams.get("class") || "";
  const [userSubscriptions, setUserSubscriptions] = useState<any[]>([]);
  
  // Initialize form with default values
  const form = useForm<FormValues>({
    resolver: zodResolver(bookingSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      createAccount: false,
      useSubscription: "",
      isGroupBooking: false,
      groupSize: 1,
      groupNames: [],
      useGiftCard: false,
      giftCardCode: "",
    },
  });
  
  // Load user data using our new hook
  const { userData } = useLoadUserData();
  
  // Initialize form with user data
  useInitializeForm(form, userData);
  
  // Fetch user subscriptions separately
  useEffect(() => {
    const loadSubscriptions = async () => {
      const userId = user?.id || localStorage.getItem('userId');
      if (!userId) return;
      
      try {
        const subscriptions = await fetchUserSubscriptions(userId);
        const activeSubscriptions = subscriptions.filter(sub => 
          sub.active && 
          sub.remainingClasses > 0 && 
          !isPast(new Date(sub.expirationDate))
        );
        
        setUserSubscriptions(activeSubscriptions);
      } catch (subscriptionError) {
        console.error('Error fetching subscriptions:', subscriptionError);
      }
    };
    
    loadSubscriptions();
  }, [user]);

  return {
    form,
    classId,
    userSubscriptions
  };
};
